/**
 * Auto-corrección del subdominio de tenant (spec 2026-08-11).
 *
 * Función PURA a propósito: no toca `window` ni Angular. El salto entre orígenes
 * no es simulable en Karma, así que todo lo que sí se puede probar vive acá y el
 * `location.replace()` queda reducido a una línea en `app.config.ts`.
 *
 * Esto NO es control de acceso: el aislamiento entre tenants lo dan el claim
 * `tenant_id` del JWT y la RLS. Acá sólo se corrige una URL que miente.
 */

/** Mismo regex que `SubdomainSchema` del backend. No inventar otro. */
const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/** Marca «ya se intentó redirigir». Va en la URL porque debe cruzar orígenes. */
export const TENANT_REDIRECT_PARAM = '_tr';

export interface TenantRedirectInput {
  /** `window.location.href` completo. */
  href: string;
  /** `environment.domainSuffix`. */
  suffix: string;
  /** `me.tenant?.subdomain`, o `null` para super admin / estación / despachador. */
  tenantSubdomain: string | null;
  /** `environment.tenantSubdomainRedirect && !environment.useMocks`. */
  enabled: boolean;
}

function parse(href: string): URL | null {
  try {
    return new URL(href);
  } catch {
    return null;
  }
}

/** URL a la que reubicar al usuario, o `null` si debe quedarse donde está. */
export function resolveTenantRedirect(input: TenantRedirectInput): string | null {
  const { href, suffix, tenantSubdomain, enabled } = input;

  if (!enabled) return null;
  if (!tenantSubdomain) return null;
  // Un subdominio corrupto construiría una URL inválida y dejaría al usuario
  // fuera del portal sin forma de volver. Preferimos no movernos.
  if (!SUBDOMAIN_RE.test(tenantSubdomain)) return null;

  const url = parse(href);
  if (!url) return null;
  if (url.searchParams.get(TENANT_REDIRECT_PARAM) === '1') return null;

  // Fuera del dominio del producto (localhost, IP, preview) no se toca nada:
  // si no, el override `?tenant=` de desarrollo quedaría inutilizable.
  const host = url.hostname;
  const isApex = host === suffix;
  if (!isApex && !host.endsWith(`.${suffix}`)) return null;

  const currentSlug = isApex ? null : host.slice(0, -1 - suffix.length);
  if (currentSlug === tenantSubdomain) return null;

  url.hostname = `${tenantSubdomain}.${suffix}`;
  url.searchParams.set(TENANT_REDIRECT_PARAM, '1');
  return url.toString();
}

/** URL sin el marcador, o `null` si no había nada que limpiar. */
export function stripTenantRedirectParam(href: string): string | null {
  const url = parse(href);
  if (!url) return null;
  if (!url.searchParams.has(TENANT_REDIRECT_PARAM)) return null;
  url.searchParams.delete(TENANT_REDIRECT_PARAM);
  return url.toString();
}
